import type { JobFunction } from '@atlas/types'
import { useId } from 'react'
import { ConfirmDialog } from '@/components/confirm/ConfirmDialog'
import { JOB_FUNCTION_OPTIONS } from '@/lib/job-functions'
import type { UserDTO } from '@/services/user.service'

// Job-function dialog (feature-local, presentational → storiable). Mirrors
// RoleEditDialog, minus the destructive styling: job functions are peers, so no
// change between them is a demotion and none grants or removes access. Uses the
// dedicated job-function endpoint via the caller — never the profile-update route.

export interface JobFunctionEditDialogProps {
	user: UserDTO | null
	value: JobFunction
	onChange: (jobFunction: JobFunction) => void
	onConfirm: () => void
	onCancel: () => void
	isPending?: boolean
	/** Shown in place ALONGSIDE the status-bar report, so the retry control sits next to the reason. */
	errorMessage?: string | null
}

export function JobFunctionEditDialog({
	user,
	value,
	onChange,
	onConfirm,
	onCancel,
	isPending,
	errorMessage,
}: JobFunctionEditDialogProps) {
	const selectId = useId()
	if (!user) return null

	return (
		<ConfirmDialog
			open={!!user}
			title="Change job function"
			description={`Set what ${user.displayName} (${user.email}) does. This does not change what they can access.`}
			confirmLabel="Update job function"
			submitting={isPending}
			busyLabel="Working…"
			errorMessage={errorMessage}
			onConfirm={onConfirm}
			onCancel={onCancel}
		>
			<div className="flex flex-col text-sm">
				<label htmlFor={selectId}>Job function</label>
				<select
					id={selectId}
					className="border rounded-ds-base px-2 py-1 h-10"
					value={value}
					onChange={(e) => onChange(e.target.value as JobFunction)}
				>
					{JOB_FUNCTION_OPTIONS.map((o) => (
						<option key={o.value} value={o.value}>
							{o.label}
						</option>
					))}
				</select>
			</div>
		</ConfirmDialog>
	)
}
